import { PageFilterRef } from '../types/page-filter.ref';
import { ApplicationRef, DestroyRef, inject, Injector } from '@angular/core';
import { ModuleTranslationLoaderRegistry } from '@softapps/core';
import { PageFilter } from '../page-filter';
import { PageFilterGroup } from '../types/page-filter-group';
import { PageFilterService } from '../services/page-filter.service';
import { PageFilterSource } from '../types/page-filter-source';
import { PageFilterLink } from '../types/page-filter-link';
import { PageFilterItem } from '../types/page-filter-item';
import { FilterTemplateRepository } from '../../repositories/filter-template.repository';
import { LocalStorageFilterTemplateRepository } from '../../repositories/local-storage-filter-template.repository';
import { FilterTemplateService } from '../../services/filter-template.service';
import { SOFTLINE_TRANSLATION_MODULE_CORE_FILTER } from '../../filter.providers';

/**
 * Declares the page filter; the page renders it with `<soft-page-filter-bar [config]>`
 * (templates, pinned dropdowns, reset/open and the chip row) - nothing is registered in the shell.
 */
export interface PageFilterOptions {
  factory: (ref: PageFilterFactoryRef) => void
  injector?: Injector;
  /** group key for filter templates; enables the Vorlagen UI when a FilterTemplateRepository is provided */
  key?: string;
}

export class PageFilterFactoryRef {
  sources: PageFilterSource<any>[] = [];
  links: PageFilterLink<any, any>[] = [];
  groups: PageFilterGroup[] = [];
  items: PageFilterItem<any, any>[] = [];

  addSource(item: PageFilterSource<any>): void {
    this.sources.push(item);
  }

  addLink(item: PageFilterLink<any, any>): void {
    this.links.push(item);
  }

  addGroup(item: PageFilterGroup): void {
    this.groups.push(item);
  }

  addItem(item: PageFilterItem<any, any>): void {
    this.items.push(item);
  }
}

export function pageFilter(
  options: PageFilterOptions
): PageFilterRef {
  const filterRef = new PageFilterFactoryRef();
  options.factory(filterRef);

  const service = new PageFilterService(
    filterRef.sources,
    filterRef.groups,
    filterRef.links,
    filterRef.items,
    options.key ?? null
  )
  const injector = options?.injector ?? inject(Injector);

  // self-registered so no app has to import this lib at boot (NF boot-size gate);
  // the i18n JSON chunk loads on first page-filter use, register/load are idempotent
  const translationRegistry = injector.get(ModuleTranslationLoaderRegistry);
  translationRegistry.register(SOFTLINE_TRANSLATION_MODULE_CORE_FILTER, {
    de: () => import('../../i18n/de.json'),
    en: () => import('../../i18n/en.json'),
  });
  void translationRegistry.load(SOFTLINE_TRANSLATION_MODULE_CORE_FILTER);

  // LocalStorage is the default template store; a DI-provided FilterTemplateRepository
  // (provideFilterTemplates or a backend implementation) takes precedence
  const templateRepository = options.key
    ? injector.get(FilterTemplateRepository, null, { optional: true }) ??
      Injector.create({
        parent: injector,
        providers: [LocalStorageFilterTemplateRepository],
      }).get(LocalStorageFilterTemplateRepository)
    : null;
  const templateService = templateRepository && options.key
    ? new FilterTemplateService(templateRepository, service, options.key)
    : null;

  const pageFilter = new PageFilter(service,
    {
      injector,
      appRef: inject(ApplicationRef),
      templateService
    }
  )

  const destroyRef = inject(DestroyRef);
  destroyRef.onDestroy(() => {
    pageFilter.dispose();
  })

  return pageFilter;
}
